import { palette } from '../global/palette';
import { typography } from '../global/typography';
import type { ComponentOverride } from '../types';

export const MuiButton: ComponentOverride<'MuiButton'> = {
  MuiButton: {
    styleOverrides: {
      root: {
        ...typography.button,
        lineHeight: 'initial',
        padding: '6px 24px',
        height: '40px',
        boxShadow: 'none',
        '&:hover, &:active': {
          boxShadow: 'none',
        },
      },
      outlined: {
        color: palette.common.black,
        '&:hover': {
          backgroundColor: palette.grey[200],
        },
        '&:active, &:focus-visible': {
          backgroundColor: palette.grey[300],
        },
        ':disabled': {
          color: palette.grey[400],
        },
      },
      text: {
        color: palette.common.black,
        '&:hover': {
          backgroundColor: palette.grey[200],
        },
        '&:active, &:focus-visible': {
          backgroundColor: palette.grey[300],
        },
        ':disabled': {
          color: palette.grey[400],
        },
      },
      contained: {
        color: palette.common.white,
        backgroundColor: palette.primary.main,
        '&:hover': {
          backgroundColor: palette.primary.light,
          borderColor: palette.common.black,
        },
        '&:active, &:focus-visible': {
          backgroundColor: palette.primary.dark,
        },
        ':disabled': {
          borderColor: palette.grey[400],
          backgroundColor: palette.grey[400],
          color: palette.common.white,
        },
      },
      containedPrimary: {
        backgroundColor: palette.success.main,
        '&:hover': {
          backgroundColor: palette.success.light,
        },
        '&:active, &:focus-visible': {
          backgroundColor: palette.success.dark,
        },
      },
      containedSecondary: {
        backgroundColor: palette.error.main,
        '&:hover': {
          backgroundColor: palette.error.light,
        },
        '&:active, &:focus-visible': {
          backgroundColor: palette.error.dark,
        },
      },
      containedInfo: {
        backgroundColor: palette.info.main,
        '&:hover': {
          backgroundColor: palette.info.light,
        },
        '&:active, &:focus-visible': {
          backgroundColor: palette.info.dark,
        },
      },
      startIcon: {
        fill: palette.common.white,
      },
      sizeSmall: {
        fontSize: '14px',
        height: '32px',
        padding: '6px 16px',
      },
      iconSizeSmall: {
        '& > svg': {
          height: 16,
          width: 16,
        },
      },
      iconSizeMedium: {
        '& > svg': {
          height: 16,
          width: 16,
        },
      },
    },
    variants: [
      {
        props: { variant: 'invertedOutlined' },
        style: {
          backgroundColor: 'transparent',
          color: palette.common.white,
          border: `solid 1px ${palette.common.white}`,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: `solid 1px ${palette.common.white}`,
          },
          '&:active, &:focus-visible': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
          '&:disabled': {
            border: `solid 1px rgba(255, 255, 255, 0.5)`,
            color: 'rgba(255, 255, 255, 0.5)',
          },
        },
      },
      {
        props: { variant: 'invertedText' },
        style: {
          backgroundColor: 'transparent',
          color: palette.common.white,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
          '&:active, &:focus-visible': {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
          '&:disabled': {
            color: 'rgba(255, 255, 255, 0.5)',
          },
        },
      },
      {
        props: { variant: 'brand' },
        style: {
          backgroundColor: palette.orange.main,
          color: palette.common.white,
          '&:hover, &:focus-visible': {
            backgroundColor: palette.orange.light,
          },
          '&:disabled': {
            backgroundColor: palette.grey[400],
            color: palette.common.white,
          },
          '&:active': {
            backgroundColor: palette.orange.dark,
          },
        },
      },
    ],
    defaultProps: {
      variant: 'contained',
      disableTouchRipple: true,
      color: 'inherit',
    },
  },
};
